import React, { useMemo } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { formatCurrency } from "../../utils/formatCurrency";
import { TITULO_CARD, TITULO_ACLARACION } from "./tituloCard";
// El nombre de la forma de pago lo escribe el cliente ("Efectivo", "Cash",
// "Tarjeta Naranja"), así que va por la paleta LIBRE y no por ORIGEN. El color
// sale del nombre: si saliera de la posición, "Efectivo" cambiaría de color
// cada vez que otra forma lo pasa en monto.
import { paletaLibre } from "../Stats2/theme";
import { LEYENDA_DONA } from "./graficos";

const PaymentBreakdown = ({ livePayments, dailyDocs, period = 30, periodLabel = "" }) => {
  // Use live payments if available, otherwise aggregate from daily docs
  const entries = useMemo(() => {
    // Try live first
    if (livePayments && typeof livePayments === "object") {
      const live = Object.entries(livePayments).filter(([, v]) => v > 0);
      if (live.length > 0) return { data: live, label: "Hoy" };
    }

    // Fallback: aggregate from recent daily docs
    if (!dailyDocs?.length) return { data: [], label: "" };

    // Acepta tambien documentos MENSUALES, que traen `month` en vez de `date`:
    // asi la misma vista sirve para romper el techo de 90 dias de los diarios.
    const recent = [...dailyDocs]
      .sort((a, b) => String(b.date || b.month || "").localeCompare(String(a.date || a.month || "")))
      .slice(0, period);

    const agg = {};
    recent.forEach((d) => {
      if (d.payments && typeof d.payments === "object") {
        Object.entries(d.payments).forEach(([method, val]) => {
          agg[method] = (agg[method] || 0) + (val || 0);
        });
      }
    });

    return {
      data: Object.entries(agg).filter(([, v]) => v > 0),
      label: periodLabel || `últimos ${recent.length} días`,
    };
  }, [livePayments, dailyDocs, period, periodLabel]);

  const options = useMemo(
    () => ({
      chart: { type: "donut", fontFamily: "inherit", animations: { dynamicAnimation: { enabled: false } } },
      labels: entries.data.map(([k]) => k),
      colors: (() => {
        // La paleta se arma con las formas que se van a dibujar: así dos formas
        // distintas no pueden caer en el mismo color dentro de la misma dona.
        const color = paletaLibre(entries.data.map(([nombre]) => nombre));
        return entries.data.map(([nombre]) => color(nombre));
      })(),
      // La forma de pago sólo está escrita en la leyenda: sin ella, la dona
      // es un anillo de colores anónimos.
      legend: LEYENDA_DONA,
      tooltip: {
        y: { formatter: (v) => formatCurrency(v, true) },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "60%",
            labels: {
              show: true,
              value: {
                fontSize: "14px",
                fontWeight: 600,
                formatter: (val) => formatCurrency(Number(val), true),
              },
              total: {
                show: true,
                label: "Total",
                fontSize: "13px",
                formatter: (w) => {
                  const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                  return formatCurrency(total, true);
                },
              },
            },
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val.toFixed(0)}%`,
        style: { fontSize: "11px" },
      },
    }),
    [entries.data]
  );

  const series = useMemo(() => entries.data.map(([, v]) => v), [entries.data]);

  if (!entries.data.length) {
    return (
      <Card className="shadow-sm h-100">
        <CardHeader className="bg-white py-3">
          <h6 className="mb-0" style={TITULO_CARD}>
            <span className="material-symbols-rounded me-1" style={{ fontSize: 16, verticalAlign: "middle" }}>credit_card</span>
            Cómo te pagan
          </h6>
        </CardHeader>
        <CardBody className="d-flex align-items-center justify-content-center text-center text-muted px-4" style={{ minHeight: 200, fontSize: 13 }}>
          Todavía no hay cobros registrados en este período.
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm h-100">
      <CardHeader className="bg-white py-3">
        <h6 className="mb-0" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 16, verticalAlign: "middle" }}>credit_card</span>
          Cómo te pagan
          <span className="ms-1" style={TITULO_ACLARACION}>
            ({entries.label})
          </span>
        </h6>
      </CardHeader>
      <CardBody>
        <ReactApexChart options={options} series={series} type="donut" height={320} />
      </CardBody>
    </Card>
  );
};

export default PaymentBreakdown;
